// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '@/services/authService';
import { UserGroup } from '@/constants/new-menu/types';

interface Group {
    id: number;
    documentId: string;
    name: string;
    code: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string | null;
    registrationType: string;
    isActive: boolean;
    registrationMessage: string | null;
    order: number | null;
}

interface Role {
    id: number;
    documentId: string;
    name: string;
    description: string;
    type: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string | null;
}

interface User {
    id: number;
    documentId: string;
    username: string;
    email: string;
    provider: string;
    confirmed: boolean;
    blocked: boolean;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string | null;
    user_profile: any | null;
    groups: Group[];
    role: Role;
    roleFunctionCodes?: string[];
    roles?: string[]; // Thêm roles được tạo từ groups
    firstName?: string;
    lastName?: string;
    fullName?: string;
    avatar?: string;
    displayName?: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    userGroupCodes: UserGroup[];  // Giữ lại thuộc tính này
    login: (identifier: string, password: string) => Promise<void>;
    register: (userData: any) => Promise<void>;
    logout: () => void;
    updateContext: (res: any) => void;
    refreshUserData: () => Promise<void>; // Thêm hàm làm mới dữ liệu
}

// Map từ Group Code sang Role
const GROUP_TO_ROLE_MAP: Record<string, string[]> = {
    'PRIMARY_USERS': ['right_owner', 'licensee'],
    'ADMIN_USERS': ['admin', 'staff'],
    'MONITORING_TEAM': ['monitor'],
    'LEGAL_TEAM': ['legal'],
    'EDUCATION_USERS': [], // Base group, không thêm role cụ thể
    'TEACHERS': ['teacher'],
    'STUDENTS': ['student'],
    'EDUCATION_ADMIN': ['education_admin'],
    'BUSINESS_DEVELOPMENT': ['business_developer'],
    'RISK_SECURITY': ['security_analyst'],
    'SUPPORT_MONITORING': ['support_specialist', 'legal'],
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Hàm chuyển đổi từ groups sang roles
    const mapGroupsToRoles = (groups: Group[] | undefined): string[] => {
        if (!groups || groups.length === 0) return [];

        const roles = new Set<string>();

        groups.forEach(group => {
            const groupRoles = GROUP_TO_ROLE_MAP[group.code] || [];
            groupRoles.forEach(role => roles.add(role));
        });

        // Xử lý các trường hợp đặc biệt
        const groupCodes = groups.map(g => g.code);

        // Nếu người dùng thuộc cả EDUCATION_USERS và TEACHERS
        if (groupCodes.includes('EDUCATION_USERS') && groupCodes.includes('TEACHERS')) {
            roles.add('teacher');
        }

        // Nếu người dùng thuộc cả EDUCATION_USERS và STUDENTS
        if (groupCodes.includes('EDUCATION_USERS') && groupCodes.includes('STUDENTS')) {
            roles.add('student');
        }

        // Nếu người dùng thuộc cả EDUCATION_USERS và EDUCATION_ADMIN
        if (groupCodes.includes('EDUCATION_USERS') && groupCodes.includes('EDUCATION_ADMIN')) {
            roles.add('education_admin');
        }

        return Array.from(roles);
    };

    // Xử lý dữ liệu người dùng, thêm roles dựa trên groups
    const processUserData = (userData: User | null): User | null => {
        if (!userData) return null;

        const roles = mapGroupsToRoles(userData.groups);
        const fullName = userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.username;

        return {
            ...userData,
            roles,
            fullName
        };
    };

    // Hàm tiện ích để lấy mã nhóm
    const getUserGroupCodes = (user: User | null): string[] => {
        if (!user || !user.groups) return [];
        return user.groups.map(group => group.code);
    };

    // Kiểm tra trạng thái đăng nhập khi component được mount
    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                setIsLoading(true);

                // Kiểm tra token có tồn tại không
                const token = localStorage.getItem('token');
                if (!token) {
                    setUser(null);
                    return;
                }

                // Kiểm tra token có hợp lệ không
                const isValidToken = authService.isTokenValid();
                if (!isValidToken) {
                    authService.logout();
                    setUser(null);
                    setIsLoading(false);
                    return;
                }

                // Lấy thông tin người dùng
                const userData = await authService.getUserWithProfileAndGroups();

                // Xử lý dữ liệu người dùng, thêm roles và fullName
                const processedUser = processUserData(userData);
                setUser(processedUser);

            } catch (err) {
                console.error('Auth check error:', err);
                // Token không hợp lệ hoặc đã hết hạn
                authService.logout();
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuthStatus();
    }, []);

    // Hàm đăng nhập
    const handleLogin = async (identifier: string, password: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authService.login({ identifier, password });
            if (response.jwt) {
                localStorage.setItem('token', response.jwt);
            }

            // Xử lý dữ liệu người dùng, thêm roles và fullName
            const processedUser = processUserData(response.user);
            setUser(processedUser);

            console.log("Login successful:", {
                username: processedUser?.username,
                groups: processedUser?.groups?.map(g => g.code),
                roles: processedUser?.roles
            });
        } catch (err: any) {
            console.error("Login error:", err);
            setError(err.response?.data?.error?.message || 'Đăng nhập thất bại');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Hàm đăng ký
    const handleRegister = async (userData: any) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authService.register(userData);

            // Xử lý dữ liệu người dùng, thêm roles và fullName
            const processedUser = processUserData(response.user);
            setUser(processedUser);

        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Đăng ký thất bại');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Hàm đăng xuất
    const handleLogout = () => {
        authService.logout();
        setUser(null);
    };

    // Hàm cập nhật context
    const handleUpdateContext = async (res: any) => {
        setIsLoading(true);
        setError(null);

        if (res.jwt) {
            localStorage.setItem('token', res.jwt);

            // Xử lý dữ liệu người dùng, thêm roles và fullName
            const processedUser = processUserData(res.user);
            setUser(processedUser);

            // console.log("Context updated:", {
            //     username: processedUser?.username,
            //     groups: processedUser?.groups?.map(g => g.code),
            //     roles: processedUser?.roles
            // });

            setIsLoading(false);
        }
    };

    // Hàm làm mới dữ liệu người dùng
    const refreshUserData = async () => {
        try {
            setIsLoading(true);

            // Kiểm tra token có tồn tại không
            const token = localStorage.getItem('token');
            if (!token) {
                console.log('Token không tồn tại');
                setUser(null);
                return;
            }

            // Kiểm tra token có hợp lệ không
            const isValidToken = authService.isTokenValid();
            if (!isValidToken) {
                console.log('Token không hợp lệ');
                authService.logout();
                setUser(null);
                return;
            }

            // Lấy thông tin người dùng
            const userData = await authService.getUserWithProfileAndGroups();

            // Xử lý dữ liệu người dùng, thêm roles và fullName
            const processedUser = processUserData(userData);
            setUser(processedUser);

        } catch (err) {
            console.error('Refresh user data error:', err);
            setError('Không thể cập nhật thông tin người dùng.');
        } finally {
            setIsLoading(false);
        }
    };

    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        userGroupCodes: getUserGroupCodes(user),
        login: handleLogin,
        register: handleRegister,
        logout: handleLogout,
        updateContext: handleUpdateContext,
        refreshUserData
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};